import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { lovable } from '@/integrations/lovable';
import { useToast } from '@/hooks/use-toast';

interface UserRole {
  role: 'doctor' | 'admin' | 'master';
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  userRole: UserRole | null;
  loading: boolean;
  signUp: (email: string, password: string, name?: string, phone?: string) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signInWithGoogle: () => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  isAdmin: boolean;
  isMaster: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  // Resgatar token de oferta pendente (OAuth flow)
  const redeemPendingOfferToken = useCallback(async (): Promise<boolean> => {
    const pendingToken = sessionStorage.getItem('pendingOfferToken');
    if (!pendingToken) return false;
    
    try {
      const { data, error } = await supabase.functions.invoke('referral-redeem', {
        body: { token: pendingToken }
      });
      
      if (!error && data?.redeemed) {
        toast({
          title: '🎁 Desconto aplicado!',
          description: `Você ganhou ${data.discount.value}% de desconto por ${data.discount.cycles} meses! Complete seu perfil para contratar.`,
        });
        return true;
      }
    } catch (err) {
      console.error('Erro ao resgatar oferta pendente:', err);
    } finally {
      sessionStorage.removeItem('pendingOfferToken');
    }
    return false;
  }, [toast]);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);

        if (session?.user) {
          // Marcar como nova sessão se for SIGNED_IN (login/signup)
          const isNewSession = event === 'SIGNED_IN';
          
          // Redirecionar pós-login OAuth se houver rota salva
          if (isNewSession) {
            const postLoginRedirect = sessionStorage.getItem('postLoginRedirect');
            if (postLoginRedirect) {
              sessionStorage.removeItem('postLoginRedirect');
              sessionStorage.setItem('__authRedirect', postLoginRedirect);
            }
          }
          
          setTimeout(() => {
            fetchUserRole(session.user.id, isNewSession);
          }, 0);
        } else {
          setUserRole(null);
          setLoading(false);
        }
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchUserRole(session.user.id);
      } else {
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const fetchUserRole = async (userId: string, isNewSession = false) => {
    try {
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) {
        console.error('Error fetching user role:', error);
      }
      
      setUserRole(data ? { role: data.role as 'doctor' | 'admin' | 'master' } : { role: 'doctor' });
      
      // Se é uma nova sessão (login), verificar token de oferta pendente
      if (isNewSession) {
        const hadOffer = await redeemPendingOfferToken();
        // Se resgatou oferta, redirecionar para /perfil para completar dados
        if (hadOffer) {
          sessionStorage.setItem('__authRedirect', '/perfil');
          return;
        }
        
        // Verificar se o perfil está incompleto (sem telefone) - especialmente para OAuth
        const { data: profileData } = await supabase
          .from('profiles')
          .select('phone, created_at')
          .eq('id', userId)
          .single();
        
        // Calcular se é um usuário recém-criado (últimos 10 minutos ou conta < 24h sem telefone)
        const profileCreatedAt = profileData?.created_at ? new Date(profileData.created_at).getTime() : 0;
        const isRecentlyCreated = profileCreatedAt > 0
          ? (Date.now() - profileCreatedAt) < 10 * 60 * 1000
          : false;
        const isNewAccountWithoutPhone = profileCreatedAt > 0
          ? (Date.now() - profileCreatedAt) < 24 * 60 * 60 * 1000
          : false;

        // Exigir telefone para: novos usuários (< 10 min) OU contas < 24h criadas via OAuth (sem telefone)
        const needsPhone = !profileData?.phone && (isRecentlyCreated || isNewAccountWithoutPhone);
        if (needsPhone) {
          sessionStorage.setItem('requirePhoneCompletion', 'true');
          // Não redireciona mais — o PhoneRequiredModal abre inline na página atual
          return;
        }
      }
    } catch (err) {
      console.error('Error in fetchUserRole:', err);
      setUserRole({ role: 'doctor' });
    } finally {
      setLoading(false);
    }
  };

  const signUp = async (email: string, password: string, name?: string, phone?: string) => {
    const redirectUrl = `${window.location.origin}/`;
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: { 
          name: name || email.split('@')[0],
          phone: phone || null
        }
      }
    });
    
    return { error };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    return { error };
  };

  const signInWithGoogle = async () => {
    // Salvar rota de destino pós-login para redirecionar depois
    const pendingToken = sessionStorage.getItem('pendingOfferToken');
    const redirectPath = pendingToken ? '/perfil' : '/consultas';
    sessionStorage.setItem('postLoginRedirect', redirectPath);
    
    const result = await lovable.auth.signInWithOAuth('google', {
      redirect_uri: window.location.origin,
    });
    return { error: result.error ?? null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setUserRole(null);
  };

  const isAdmin = userRole?.role === 'admin' || userRole?.role === 'master';
  const isMaster = userRole?.role === 'master';

  return (
    <AuthContext.Provider value={{ 
      user, 
      session, 
      userRole, 
      loading, 
      signUp, 
      signIn, 
      signInWithGoogle,
      signOut,
      isAdmin,
      isMaster 
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

/** Hook seguro para componentes que podem ser renderizados fora do AuthProvider (ex.: ChatWidget) */
export function useOptionalAuth() {
  const context = useContext(AuthContext);
  return context ?? null;
}
